import Grid from '@material-ui/core/Grid';
import { makeStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import FieldSelect from '@react-form-fields/material-ui/components/Select';
import { dateFormat, dateParse } from 'formatters/date';
import { getStatusLabel } from 'interfaces/dashboard';
import { statuses } from 'interfaces/file';
import { IVenture } from 'interfaces/venture';
import React, { memo, useCallback, useMemo, useState } from 'react';
import {
  Bar,
  BarChart,
  Legend,
  ResponsiveContainer,
  Tooltip,
  TooltipFormatter,
  XAxis,
  YAxis,
} from 'recharts';

import Card from '../Component/Card';

interface IProps {
  metrics: any;
}

const useStyles = makeStyles((theme) => ({
  table: {
    backgroundColor: theme.palette.background.paper,
    borderRadius: theme.spacing(1),
    padding: theme.spacing(2),
  },
  content: {
    alignSelf: 'center',
  },
  middleText: theme.typography.h5,
  iconStatus: {
    verticalAlign: 'middle',
  },
  label: {
    ...theme.typography.h6,
    fill: '#000000',
    opacity: 0.6,
  },
  select: {
    borderRadius: 4,
    position: 'relative',
    backgroundColor: theme.palette.secondary.light,
    color: theme.palette.primary.light,
    fontSize: 16,
    padding: '6px 26px 6px 12px',
    transition: theme.transitions.create(['border-color', 'box-shadow']),
    '&:focus': {
      borderRadius: 4,
      borderColor: '#80bdff',
      boxShadow: '0 0 0 0.2rem rgba(0,123,255,.25)',
    },
  },
  typography: {
    fontWeight: 'bold',
    color: '#fff',
  },
}));

const AxisTick = (props: any) => {
  const { payload, verticalAnchor, visibleTicksCount, ...others } = props;
  return (
    <text
      {...others}
      className={'recharts-text recharts-cartesian-axis-tick-value'}
    >
      <Typography component="tspan" variant="caption" dy="0.71em">
        {dateFormat(dateParse(payload.value, 'yyyy-MM'), 'MMM/yy')}
      </Typography>
    </text>
  );
};

const HistoricVentureStatus = memo<IProps>((props: IProps) => {
  const { metrics } = props;
  const classes = useStyles();
  const [statusFilter, setStatusFilter] = useState<statuses | 'CONSOLIDATED'>(
    statuses.APPROV
  );
  const statusOptions = [...Object.keys(statuses), 'CONSOLIDATED'].map((s) => ({
    value: s,
    label: getStatusLabel(s),
  }));

  const handleChangeStatus = (value: statuses) => {
    setStatusFilter(value);
  };

  const dataSheet = metrics.status.find(
    (i: any) => i.status === statusFilter
  ).data;

  const findVentureName = useCallback(
    (id: string) => metrics.ventures.find((i: any) => i.id === Number(id)).name,
    [metrics.ventures]
  );

  const formatterTooltip: TooltipFormatter = useMemo(
    () => (value, name) =>
      [
        'CONSOLIDATED' === statusFilter ? Number(value).toFixed(2) + '%' : value,
        findVentureName(name),
      ],
    [findVentureName, statusFilter]
  );

  const formatterLegend = (name: string) => findVentureName(name);
  const formatterTicker = (date: string) =>
    dateFormat(dateParse(date, 'yyyy-MM'), 'MMM/yy');
  const getColor = (index: number): string => {
    const colors: string[] = [
      '#8e44ad',
      '#27ae60',
      '#c0392b',
      '#f39c12',
      '#2c3e50',
      '#2980b9',
      '#B33771',
      '#F97F51',
    ];
    if (index < colors.length) {
      return colors[index];
    }
    return `#${Math.floor(Math.random() * 16777215)
      .toString(16)
      .padStart(6, '0')}`;
  };

  return (
    <Card style={{ background: '#636565' }}>
      <Grid container justify="space-between" spacing={1}>
        <Grid item>
          <Typography className={classes.typography} variant="subtitle1">
            Histórico
          </Typography>
        </Grid>
        <Grid item xs={4}>
          <FieldSelect
            value={statusFilter}
            options={statusOptions}
            onChange={handleChangeStatus}
            classes={{ select: classes.select }}
            fullWidth
            margin="none"
          />
        </Grid>
        <Grid item xs={12}>
          <div className={classes.table}>
            <ResponsiveContainer width="100%" height={180}>
              <BarChart data={dataSheet}>
                <YAxis axisLine={false} tickLine={false} width={35} />
                <XAxis
                  dataKey="month"
                  axisLine={false}
                  tickLine={false}
                  tickFormatter={formatterTicker}
                  tick={<AxisTick />}
                />
                <Tooltip
                  labelFormatter={formatterTicker}
                  formatter={formatterTooltip}
                />
                <Legend formatter={formatterLegend} />
                {metrics.ventures.map((v: Partial<IVenture>, index: number) => (
                  <Bar
                    key={`line-${v.id}`}
                    dataKey={v.id}
                    fill={getColor(index)}
                  />
                ))}
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Grid>
      </Grid>
    </Card>
  );
});

export default HistoricVentureStatus;
